<template>
  <div class="riw-panel-body">
    <!-- AVA-602 LEG-5: known before any file is read, so it shows as soon as the dialog opens. -->
    <div v-if="modeUnset" class="riw-form-section">
      <div class="riw-callout riw-callout--err" role="alert">
        <v-icon class="riw-callout__icon">mdi-lock-alert-outline</v-icon>
        <div class="riw-callout__body">
          <span class="riw-callout__title">Register mode not set</span>
          This Facility Group register already has {{ formatNumber(registerRowCount) }} rows but no Shared/Separated mode.
          An administrator must set its mode before importing.
        </div>
      </div>
    </div>

    <div class="riw-form-section">
      <div class="riw-section-label">
        <span id="riw-upload-label" class="riw-label-text">Upload Data File</span>
        <span class="riw-badge riw-badge--req">Required</span>
      </div>

      <button
        v-if="zoneState === 'empty' || zoneState === 'dragover'"
        type="button"
        class="riw-upload-zone"
        :class="{ 'is-dragover': zoneState === 'dragover' }"
        aria-labelledby="riw-upload-label"
        @click="pick"
        @dragover.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        @drop.prevent="onDrop">
        <v-icon class="riw-upload-zone__icon">{{ zoneState === 'dragover' ? 'mdi-tray-arrow-down' : 'mdi-cloud-upload-outline' }}</v-icon>
        <span class="riw-upload-zone__text">
          <span class="riw-upload-zone__title">{{ zoneState === 'dragover' ? 'Release to upload' : 'Drop your Excel file here' }}</span>
          <span class="riw-upload-zone__hint">or click to browse</span>
        </span>
        <span class="riw-upload-zone__formats"><span class="riw-format-badge">.xlsx</span><span class="riw-format-badge">.xls</span></span>
      </button>

      <div v-else-if="zoneState === 'processing'" class="riw-upload-zone has-file" aria-live="polite">
        <v-progress-circular indeterminate color="primary" size="28" width="3" />
        <span class="riw-upload-zone__text">
          <span class="riw-upload-zone__title">Processing {{ file ? file.name : 'file' }}…</span>
          <span class="riw-upload-zone__hint">Analyzing structure and extracting data</span>
        </span>
      </div>

      <div v-else-if="zoneState === 'error'" class="riw-upload-zone is-error">
        <v-icon class="riw-upload-zone__icon">mdi-file-alert-outline</v-icon>
        <span class="riw-upload-zone__text">
          <span class="riw-upload-zone__title">{{ file ? file.name : 'File' }}</span>
          <span class="riw-upload-zone__hint">We couldn’t read this file</span>
        </span>
        <v-btn small outlined color="primary" @click="pick">
          <v-icon left small>mdi-swap-horizontal</v-icon>Choose another file
        </v-btn>
      </div>

      <div v-else class="riw-upload-zone has-file">
        <v-icon class="riw-upload-zone__icon">mdi-file-excel-outline</v-icon>
        <span class="riw-upload-zone__text">
          <span class="riw-upload-zone__title">{{ file.name }}</span>
          <span class="riw-upload-zone__hint riw-num">{{ formatFileSize(file.size) }}<template v-if="summary && summary.sheet"> · Sheet “{{ summary.sheet }}”</template></span>
        </span>
        <v-btn icon small aria-label="Remove file" title="Remove file" @click="$emit('clear')">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <input ref="fileInput" type="file" accept=".xlsx,.xls" class="riw-hidden-input" @change="onInputChange" />
    </div>

    <!-- Shown only for a Facility register: its Tag column links only to Tags in its own Facility
         (RegisterDataService.ProcessTagLinkingAsync uses register.FacilityId). -->
    <div v-if="showTagLinkingNote" class="riw-form-section">
      <div class="riw-callout riw-callout--info">
        <v-icon class="riw-callout__icon">mdi-information-outline</v-icon>
        <div class="riw-callout__body">
          <span class="riw-callout__title">Tag Linking Information</span>
          Tags are linked by name, only to Tags in this register's Facility.
        </div>
      </div>
    </div>

    <div v-if="modeFirstImport && zoneState === 'parsed'" class="riw-form-section">
      <div class="riw-callout riw-callout--info">
        <v-icon class="riw-callout__icon">mdi-call-split</v-icon>
        <div class="riw-callout__body">
          <span class="riw-callout__title">This import sets the register mode</span>
          The register is empty. In the next step, choosing a Facility column makes it Separated; leaving it empty makes it Shared.
          The mode can’t be changed after this import.
        </div>
      </div>
    </div>

    <div v-if="zoneState === 'error' && parseError" class="riw-form-section">
      <div class="riw-callout riw-callout--err" role="alert">
        <v-icon class="riw-callout__icon">mdi-alert-circle-outline</v-icon>
        <div class="riw-callout__body">
          <span class="riw-callout__title">This file can’t be imported</span>{{ parseError }}
        </div>
      </div>
    </div>

    <template v-if="zoneState === 'parsed' && summary">
      <div class="riw-form-section">
        <div class="riw-upload-summary">
          <div class="riw-summary-card">
            <div class="riw-summary-icon riw-summary-icon--primary"><v-icon>mdi-table-row</v-icon></div>
            <div><div class="riw-summary-value">{{ formatNumber(summary.rows) }}</div><div class="riw-summary-label">Rows</div></div>
          </div>
          <div class="riw-summary-card">
            <div class="riw-summary-icon riw-summary-icon--blue"><v-icon>mdi-table-column</v-icon></div>
            <div><div class="riw-summary-value">{{ formatNumber(summary.columns.length) }}</div><div class="riw-summary-label">Columns</div></div>
          </div>
          <div class="riw-summary-card">
            <div class="riw-summary-icon riw-summary-icon--violet"><v-icon>mdi-table-check</v-icon></div>
            <div>
              <div class="riw-summary-value">{{ savedSchemaFieldCount ? 'Available' : 'None' }}</div>
              <div class="riw-summary-label">Saved schema</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="warnings.length" class="riw-form-section">
        <div class="riw-callout riw-callout--warn">
          <v-icon class="riw-callout__icon">mdi-alert-outline</v-icon>
          <div class="riw-callout__body">
            <span class="riw-callout__title">{{ warnings.length === 1 ? 'Check this before mapping' : `${warnings.length} things to check before mapping` }}</span>
            <span v-for="(message, index) in warnings" :key="index" class="riw-callout__line">{{ message }}</span>
          </div>
        </div>
      </div>

      <div class="riw-form-section">
        <div class="riw-section-label">
          <span class="riw-label-text">Columns found</span>
          <span class="riw-badge riw-badge--opt riw-num">{{ summary.columns.length }}</span>
        </div>
        <div class="riw-col-chips">
          <span v-for="column in summary.columns" :key="column" class="riw-col-chip">
            <v-icon size="14">mdi-table-column</v-icon>{{ column }}
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/** Extensions the drop zone accepts — same rule the wizard has always applied. */
const ACCEPTED_FILE = /\.(xlsx?|csv)$/i;

/**
 * Upload step: compact inline drop zone (Append Data / Task Import pattern). The file is parsed as
 * soon as it is picked — the parent does the parsing and passes the result back in.
 */
export default {
  name: 'ImportUploadStep',
  props: {
    file: { type: [File, Object], default: null },
    processing: { type: Boolean, default: false },
    parseError: { type: String, default: '' },
    /** { rows, columns: string[], sheet } once parsed. */
    summary: { type: Object, default: null },
    warnings: { type: Array, default: () => [] },
    savedSchemaFieldCount: { type: Number, default: 0 },
    showTagLinkingNote: { type: Boolean, default: false },
    modeUnset: { type: Boolean, default: false },
    modeFirstImport: { type: Boolean, default: false },
    registerRowCount: { type: Number, default: null },
  },
  data: () => ({ dragOver: false }),
  computed: {
    zoneState() {
      if (this.processing) return 'processing';
      if (this.parseError) return 'error';
      if (this.file && this.summary) return 'parsed';
      return this.dragOver ? 'dragover' : 'empty';
    },
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString('en-US');
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
    },
    pick() {
      if (this.processing) return;
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    onInputChange(event) {
      const file = event.target.files && event.target.files[0];
      if (file) this.$emit('pick', file);
    },
    onDrop(event) {
      this.dragOver = false;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (!file) return;
      if (ACCEPTED_FILE.test(file.name)) this.$emit('pick', file);
      else this.$emit('invalid-file', file);
    },
  },
};
</script>
